<template>
    <admin-page-layout>
        <template #titleName>
            WHITELIST DES UTILISATEURS POUR LA DOUBLE AUTHENTIFICATION
        </template>
        <template #content>
            <Toast />
            <ConfirmDialog group="global" :closeOnEscape="false" :closable="false" />
            <ConfirmDialog group="global-status" :closeOnEscape="false" :closable="false" />
            <div class="button-container">
                <div class="dropdown-button">
                    <Button
                        label="Ajouter un utilisateur à la whitelist"
                        icon="pi pi-plus"
                        @click="showDialog = true"
                        class="custom-button button-add"
                    />
                </div>
            </div>
            <TabView>
                <TabPanel header="Par IP">
                    <TabByIP @confirmDelete="confirmDeleteDialog" @confirmStatusChange="confirmStatusChangeDialog"/>
                </TabPanel>
                <TabPanel header="Par société">
                    <TabByCompany @confirmDelete="confirmDeleteDialog" @confirmStatusChange="confirmStatusChangeDialog" @update-table-data="updateTableData"/>
                </TabPanel>
                <TabPanel header="Par email">
                    <TabByEmail  @confirmDelete="confirmDeleteDialog" @confirmStatusChange="confirmStatusChangeDialog"/>
                </TabPanel>
            </TabView>
            <WhiteListModal
                v-model:visible="showDialog"
                :header="dialogHeader"
                :selected-method="selectedMethod"
                :table-data='tableData'
                @update:selected-method="updateSelectedMethod"
                @confirm="onAddConfirm"
                :is-submitting="isSubmitting"
            />
        </template>
    </admin-page-layout>
</template>

<script lang="ts" setup>
import { ref, computed } from 'vue';
import AdminPageLayout from "@/layouts/default/admin/AdminPageLayout.vue";
import TabByIP from '@/components/securite/whitelist/TabByIP.vue';
import TabByCompany from '@/components/securite/whitelist/TabByCompany.vue';
import TabByEmail from '@/components/securite/whitelist/TabByEmail.vue';
import WhiteListModal from '@/components/modal/whiteList/WhiteListModal.vue';
import ApiService from '@/_services/api.service';
import { useToast } from 'primevue/usetoast';
import Button from 'primevue/button';
import TabView from 'primevue/tabview';
import TabPanel from 'primevue/tabpanel';
import ConfirmDialog from 'primevue/confirmdialog';
import { useConfirm } from 'primevue/useconfirm';
import { CompanyWhitelist } from '@/models/securite/whitelist/IcompanyWhiteLis';

const toast = useToast();
const showDialog = ref(false);
const selectedMethod = ref<string>('IP');
const confirm = useConfirm();
const tableData = ref<CompanyWhitelist[]>([]);

const dialogHeader = computed(() => {
    const method = selectedMethod.value;
    let methodText = '';

    switch (method) {
        case 'IP':
            methodText = 'IP';
            break;
        case 'Company':
            methodText = 'Société';
            break;
        case 'Email':
            methodText = 'Email';
            break;
    }
    return `Ajouter par ${methodText}`;
});
const isSubmitting = ref(false);
const onAddConfirm = async (data: any) => {
    if (isSubmitting.value) return;
    isSubmitting.value = true;
    
    let endpoint = '';
    let postData = {};
    const error = ref<string>("");

    switch (selectedMethod.value) {
        case 'IP':
            endpoint = 'ip_white_lists';
            postData = {
                ipAddress: data.address,
                company: data.company,
                description: data.description,
                requestedBy: data.requestedBy
            };
            break;
        case 'Company':
            endpoint = 'company_white_lists';
            postData = {
                company: data.companyName,
                description: data.description,
                requestedBy: data.requestedBy
            };
            break;
        case 'Email':
            endpoint = 'user_white_lists';
            postData = {
                email: data.email,
                description: data.description,
                requestedBy: data.requestedBy
            };
            break;
    }
    isSubmitting.value = true;
    try {
        const response = await ApiService.post(endpoint, postData);
        showDialog.value = false;
        toast.add({ severity: 'success', summary: 'Succès', detail: 'Élément ajouté avec succès!', life: 3000 });
        window.location.reload();
    } catch (err: any) {
        error.value = `L'adresse mail fournie est invalide. Veuillez vérifier et réessayer.`;
        toast.add({ severity: 'error', summary: 'Erreur', detail: error.value, life: 3000 });    
    } finally {
        isSubmitting.value = false;
    }
};

const updateSelectedMethod = (newMethod: string) => {
    selectedMethod.value = newMethod;
};

const updateTableData = (newData: CompanyWhitelist[]) => {
    tableData.value = newData;
};

const confirmDeleteDialog = (data) => {
    confirm.require({
        group: 'global',
        message: `Êtes-vous sûr de vouloir supprimer cet élément ?`,
        header: 'Confirmation',
        icon: 'pi pi-exclamation-triangle',
        acceptLabel: "Oui",
        rejectLabel: "Non",
        acceptClass: 'my-custom-accept-class',
        accept: async () => {
            try {
                toast.add({ severity: 'success', summary: 'Supprimé', detail: 'Élément supprimé avec succès', life: 3000 });
            } catch (error) {
                toast.add({ severity: 'error', summary: 'Erreur', detail: 'Erreur lors de la suppression', life: 3000 });
            }
        }
    });
};
const confirmStatusChangeDialog = (data) => {
    confirm.require({
        group: 'global-status',
        message: `Êtes-vous sûr de vouloir changer le statut de cet élément ?`,
        header: 'Confirmation',
        icon: 'pi pi-exclamation-triangle',
        acceptLabel: "Oui",
        rejectLabel: "Non",
        accept: async () => {
            try {
                toast.add({ severity: 'success', summary: 'Succès', detail: `Le statut a bien été modifié`, life: 3000 });
            } catch (error) {
                toast.add({ severity: 'error', summary: 'Erreur', detail: 'Erreur lors de la modification', life: 3000 });
            }
        }
    });
};
</script>

<style scoped>
.button-container {
    margin-bottom: 1em;
    text-align: right;
}

.custom-button {
    background-color: #007bff;
    color: #ffffff;
    border: none;
}

.custom-button:hover {
    background-color: #0056b3;
}

.dropdown-button {
    display: inline-block;
    position: relative;
}
.my-custom-accept-class {
    background-color: red !important;
    color: white !important;
}
</style>
